import { directApi } from './api';

/**
 * Services for interacting with the background tag extraction system
 */

// Endpoint path constants (following DRY principles)
// Note: /api/v2 prefix is already included in the directApi baseURL
const ENDPOINTS = {
  EXTRACT_TAGS: (itemId) => `/tag-tasks/extract-tags/${itemId}`,
  TASK_STATUS: (taskId) => `/tag-tasks/status/${taskId}`,
  ITEM_TAGS: (itemId) => `/tag-tasks/item-tags/${itemId}`
};

const tagTasksService = {
  /**
   * Queue a tag extraction task for an item
   * 
   * @param {number} itemId - Item ID to extract tags for
   * @returns {Promise<Object>} - Response with task ID
   */
  queueTagExtraction: async (itemId) => {
    try {
      console.log(`Attempting to queue tag extraction for item ${itemId} with endpoint ${ENDPOINTS.EXTRACT_TAGS(itemId)}`);
      // Use directApi to ensure correct backend URL with port 8000
      const response = await directApi.post(ENDPOINTS.EXTRACT_TAGS(itemId));
      console.log('Tag extraction queued successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error queueing tag extraction:', error);
      console.error('Request URL was:', ENDPOINTS.EXTRACT_TAGS(itemId));
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      throw error;
    }
  },

  /**
   * Check the status of a tag extraction task
   * 
   * @param {string} taskId - ID of the task to check
   * @returns {Promise<Object>} - Task status information
   */
  getTaskStatus: async (taskId) => {
    try {
      // Use directApi to ensure correct backend URL with port 8000
      const response = await directApi.get(ENDPOINTS.TASK_STATUS(taskId));
      return response.data;
    } catch (error) {
      console.error('Error checking tag task status:', error);
      throw error;
    }
  },

  /**
   * Get the current tags for an item
   * 
   * @param {number} itemId - Item ID to get tags for
   * @returns {Promise<Object>} - Item tags
   */
  getItemTags: async (itemId) => {
    try {
      // Use directApi to ensure correct backend URL with port 8000
      const response = await directApi.get(ENDPOINTS.ITEM_TAGS(itemId));
      return response.data;
    } catch (error) {
      console.error('Error getting item tags:', error);
      throw error;
    }
  },

  /**
   * Poll a task status until completion or failure
   * 
   * @param {string} taskId - ID of the task to poll
   * @param {number} intervalMs - Polling interval in milliseconds
   * @param {number} timeoutMs - Maximum time to poll in milliseconds
   * @returns {Promise<Object>} - Final task result
   */
  pollTaskUntilComplete: async (taskId, intervalMs = 2000, timeoutMs = 60000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      let timeoutId = null;

      const checkStatus = async () => {
        try {
          const status = await tagTasksService.getTaskStatus(taskId);
          
          if (status.status === 'completed') {
            clearTimeout(timeoutId);
            resolve(status);
            return;
          }
          
          if (status.status === 'failed') {
            clearTimeout(timeoutId);
            reject(new Error(`Task failed: ${status.error}`));
            return;
          }

          if (Date.now() - startTime > timeoutMs) {
            clearTimeout(timeoutId);
            reject(new Error('Task polling timed out'));
            return;
          }

          timeoutId = setTimeout(checkStatus, intervalMs);
        } catch (error) {
          clearTimeout(timeoutId);
          reject(error);
        }
      };

      // Start polling
      timeoutId = setTimeout(checkStatus, intervalMs);
    });
  }
};

export default tagTasksService;
